/**
 * Boss move shapes, shared by the dungeon (systems/dungeon.ts) and the world bosses (systems/worldBosses.ts).
 * A scripted move (`DungeonMoveRule.scripted`: cone, ring, line, pull) winds up in the World; the owning system fixes a
 * ground shape when the wind-up begins (the telegraph the renderer fills), and when the wind-up ends it strikes
 * everyone standing in that shape. Pure functions of the rule and the positions: no state, no dice.
 */
import type { DungeonMoveRule } from '../data/dungeon';

/** A ground telegraph's shape, in map coordinates (directions are unit vectors; `angle` a half-angle in degrees). */
export type BossShape =
  | {
      readonly k: 'cone';
      readonly x: number;
      readonly y: number;
      readonly dx: number;
      readonly dy: number;
      readonly r: number;
      readonly angle: number;
    }
  | { readonly k: 'ring'; readonly x: number; readonly y: number; readonly r: number }
  | {
      readonly k: 'line';
      readonly x: number;
      readonly y: number;
      readonly dx: number;
      readonly dy: number;
      readonly len: number;
      readonly half: number;
    }
  | { readonly k: 'rect'; readonly x0: number; readonly x1: number; readonly y0: number; readonly y1: number }
  | { readonly k: 'pull'; readonly x: number; readonly y: number; readonly r: number };

interface At {
  readonly x: number;
  readonly y: number;
}

/**
 * The shape a scripted move will land on, fixed when its wind-up begins. It faces the target (else the caster's own
 * facing). `centre` moves a ring or pull off the caster (a boulder's circle on the target, a leap on the farthest
 * player); cones and lines always start at the caster.
 */
export function shapeOf(
  rule: Pick<DungeonMoveRule, 'scripted' | 'r' | 'angle' | 'len' | 'half'>,
  src: At & { readonly dirX: number; readonly dirY: number },
  tgt: At | undefined,
  centre?: At,
): BossShape | null {
  let dx = tgt ? tgt.x - src.x : src.dirX;
  let dy = tgt ? tgt.y - src.y : src.dirY;
  const l = Math.hypot(dx, dy) || 1;
  [dx, dy] = [dx / l, dy / l];
  const c = centre ?? src;
  switch (rule.scripted) {
    case 'cone':
      return { k: 'cone', x: src.x, y: src.y, dx, dy, r: rule.r ?? 4, angle: rule.angle ?? 35 };
    case 'ring':
      return { k: 'ring', x: c.x, y: c.y, r: rule.r ?? 3 };
    case 'line':
      return { k: 'line', x: src.x, y: src.y, dx, dy, len: rule.len ?? 6, half: rule.half ?? 0.6 };
    case 'pull':
      return { k: 'pull', x: c.x, y: c.y, r: rule.r ?? 10 };
  }
  return null;
}

/** Whether a point stands in a shape. */
export function inShape(s: BossShape, p: At): boolean {
  switch (s.k) {
    case 'ring':
    case 'pull':
      return Math.hypot(p.x - s.x, p.y - s.y) <= s.r;
    case 'cone': {
      const d = Math.hypot(p.x - s.x, p.y - s.y);
      if (d > s.r) return false;
      if (d < 0.5) return true;
      return ((p.x - s.x) * s.dx + (p.y - s.y) * s.dy) / d >= Math.cos((s.angle * Math.PI) / 180);
    }
    case 'line': {
      const along = (p.x - s.x) * s.dx + (p.y - s.y) * s.dy;
      const across = Math.abs((p.x - s.x) * -s.dy + (p.y - s.y) * s.dx);
      return along >= -0.5 && along <= s.len && across <= s.half;
    }
    case 'rect':
      return p.x >= s.x0 && p.x < s.x1 && p.y >= s.y0 && p.y < s.y1;
  }
}
